"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface NavItem {
  name: string
  url: string
  icon: LucideIcon
  onClick?: () => void
}

export interface NavBarProps {
  items: NavItem[]
  className?: string
  activeTab?: string
  onTabChange?: (name: string) => void
}

export function NavBar({ items, className, activeTab: controlledActiveTab, onTabChange }: NavBarProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(items[0]?.name)
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handleTabClick = (item: NavItem) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(item.name)
    }
    if (onTabChange) {
      onTabChange(item.name)
    }
    if (item.onClick) {
      item.onClick()
    }
  }

  return (
    <nav
      aria-label="Floating Navigation"
      className={cn(
        "fixed bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-50 mb-0",
        className,
      )}
    >
      <div className="flex items-center gap-1 sm:gap-2.5 bg-[#120a05]/95 border border-amber-500/25 backdrop-blur-2xl py-1.5 px-2 sm:py-1.5 sm:px-2.5 rounded-full shadow-[0_12px_40px_rgba(0,0,0,0.85)] ring-1 ring-white/5">
        {items.map((item) => {
          const Icon = item.icon
          const isActive = activeTab === item.name

          return (
            <Link
              key={item.name}
              href={item.url}
              aria-label={item.name}
              title={item.name}
              aria-current={isActive ? "page" : undefined}
              onClick={(e) => {
                if (item.url === '#' || item.url.startsWith('#') || item.onClick) {
                  e.preventDefault()
                }
                handleTabClick(item)
              }}
              className={cn(
                "relative cursor-pointer text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 sm:py-2 rounded-full transition-colors flex items-center gap-2 select-none",
                "text-stone-300 hover:text-amber-300 active:scale-95",
                isActive && "bg-amber-500/15 text-amber-300 shadow-inner",
              )}
            >
              <span className="flex items-center justify-center shrink-0" aria-hidden="true">
                <Icon size={19} strokeWidth={isActive ? 2.5 : 2} className={isActive ? "text-amber-400" : "text-stone-300"} />
              </span>
              <span className="hidden md:inline whitespace-nowrap text-xs font-bold">{item.name}</span>
              <span className="sr-only">{item.name}</span>
              {isActive && (
                <div
                  className="absolute inset-0 w-full bg-amber-400/15 rounded-full -z-10 transition-all duration-300"
                >
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-amber-400 rounded-t-full shadow-[0_0_10px_#f59e0b]">
                    <div className="absolute w-12 h-6 bg-amber-400/30 rounded-full blur-md -top-2 -left-2" />
                    <div className="absolute w-8 h-6 bg-amber-400/30 rounded-full blur-md -top-1" />
                    <div className="absolute w-4 h-4 bg-amber-400/30 rounded-full blur-sm top-0 left-2" />
                  </div>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
